import type { ChatAttempt } from './types'

export interface ChatRecovery {
  read(scope: string): Promise<ChatAttempt | null>
  write(scope: string, attempt: ChatAttempt): Promise<void>
  clear(scope: string): Promise<void>
}

function openRecovery(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    let blocked = false
    const request = indexedDB.open('vi-support-messenger', 1)
    request.onupgradeneeded = () => request.result.createObjectStore('attempts')
    request.onsuccess = () => {
      if (blocked) request.result.close()
      else resolve(request.result)
    }
    request.onerror = () =>
      reject(
        new Error(
          'Your browser could not open message recovery. Enable site storage or contact us by email.'
        )
      )
    request.onblocked = () => {
      blocked = true
      reject(new Error('Close other chat tabs and try again.'))
    }
  })
}

async function transaction<T>(
  mode: IDBTransactionMode,
  operation: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  const database = await openRecovery()
  try {
    return await new Promise((resolve, reject) => {
      const transaction = database.transaction('attempts', mode)
      const request = operation(transaction.objectStore('attempts'))
      // Wait for the transaction commit before allowing a network submission.
      transaction.oncomplete = () => resolve(request.result)
      transaction.onabort = transaction.onerror = () =>
        reject(
          new Error(
            'Your browser could not keep a recovery copy. Your message has not been discarded.'
          )
        )
    })
  } finally {
    database.close()
  }
}

export const chatRecovery: ChatRecovery = {
  async read(scope) {
    const value = await transaction<ChatAttempt | undefined>(
      'readonly',
      store => store.get(scope)
    )
    if (!value) return null
    if (
      value.version !== 1 ||
      !['Prepared', 'Submitting', 'Rejected'].includes(value.state) ||
      typeof value.uncertainHistory !== 'boolean' ||
      !['start', 'reply'].includes(value.action) ||
      !/^[a-f0-9]{32}$/.test(value.browserContext) ||
      typeof value.input?.requestId !== 'string' ||
      typeof value.input.html !== 'string'
    ) {
      throw new Error(
        'A saved chat message could not be recovered. Please contact us by email before clearing site storage.'
      )
    }
    try {
      for (const file of value.input.attachments ?? []) {
        if (
          typeof file.name !== 'string' ||
          typeof file.contentType !== 'string' ||
          typeof file.base64 !== 'string'
        ) {
          throw new Error('Invalid saved attachment')
        }
        atob(file.base64)
      }
    } catch {
      throw new Error(
        'An attachment in your saved message could not be recovered. Your saved copy has been kept; contact us by email for help.'
      )
    }
    return value
  },
  async write(scope, attempt) {
    await transaction('readwrite', store => store.put(attempt, scope))
  },
  async clear(scope) {
    await transaction('readwrite', store => store.delete(scope))
  },
}

/** Ending browser access removes saved sends from every account scope that used it. */
export async function clearChatRecovery(browserContext: string) {
  const database = await openRecovery()
  try {
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction('attempts', 'readwrite')
      const cursor = transaction.objectStore('attempts').openCursor()
      cursor.onsuccess = () => {
        const row = cursor.result
        if (!row) return
        if (row.value?.browserContext === browserContext) row.delete()
        row.continue()
      }
      transaction.oncomplete = () => resolve()
      transaction.onabort = transaction.onerror = () =>
        reject(
          new Error(
            'Browser access ended, but a saved message could not be cleared from this device.'
          )
        )
    })
  } finally {
    database.close()
  }
}
